import React from "react";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import Alert from "@mui/material/Alert";

// Custom.
import { Form, FormTabProps } from "@vocollege/components";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import {
  GET_COURSE_MOMENT,
  UPDATE_COURSE_MOMENT,
  CREATE_COURSE_MOMENT,
} from "api";
import { GET_AVAILABLE_GROUPS } from "@vocollege/app/dist/modules/VoApi/graphql";
import VoAuth from "@vocollege/app/dist/modules/VoAuth";
import { useStyles } from "./styles";
import { useStyles as useStylesForm } from "ui/styles/form";
import { SEARCH_USER } from "@vocollege/app/dist/modules/VoApi";
import { getAvailableCollegeValues } from "services/Utils";
import { getUserString } from "@vocollege/app/dist/modules/VoHelpers";
import Layout from "containers/Layout/Layout";
import { GeneralObject } from "@vocollege/app/dist/global";

const CourseMomentEdit: React.FC = () => {
  const urlParams = useParams<any>();
  const classes = useStyles();
  const classesForm = useStylesForm();
  const navigate = useNavigate();

  // Methods.

  const getInitialState = () => {
    const initialState: GeneralObject = {
      id: 0,
      title: "",
      college: "",
      orderer: "",
      start_date: "",
      end_date: "",
      notes: "",
      author: null,
      done: 0,
      stakeholders: [],
      billing_address: "",
    };
    const { id } = urlParams;
    if (id && id === "skapa") {
      initialState.author = {
        id: VoAuth.currentUser.id,
        name: VoAuth.currentUser.name,
        type: "user",
      };
    }
    return initialState;
  };

  const validateOrdererDependency = (v: any, fv: any) => {
    if (fv.orderer !== "0") {
      return true;
    }
    return v.trim() !== "";
  };

  const handleRedirect = () => {
    navigate("/utbildningar");
  };

  const getTabs = () => {
    const tabs: FormTabProps[] = [];
    tabs.push(
      {
        key: "course_moment",
        label: I18n.get.vocertCourseMoment.label,
        fields: [
          {
            name: "title",
            label: I18n.get.form.labels.title,
            type: "text",
            required: true,
            grid: {
              xs: 12,
              md: 6,
            },
            validation: {
              required: true,
            },
          },
          {
            name: "start_date",
            label: I18n.get.form.labels.startdate,
            type: "date_field",
            required: true,
            validation: {
              required: I18n.get.form.errors.required,
            },
            grid: {
              xs: 12,
              md: 3,
            },
          },
          {
            name: "end_date",
            label: I18n.get.form.labels.enddate,
            type: "date_field",
            required: true,
            validation: {
              required: I18n.get.form.errors.required,
            },
            grid: {
              xs: 12,
              md: 3,
            },
          },

          {
            name: "college",
            label: I18n.get.vocertCourseMoment.labels.collegeBelonging,
            type: "select",
            required: true,
            validation: {
              required: I18n.get.form.errors.required,
            },
            grid: {
              xs: 12,
            },
            params: {
              availableValues: getAvailableCollegeValues,
            },
          },
          {
            name: "orderer",
            label: I18n.get.vocertCourseMoment.labels.ordererCollege,
            type: "select",
            required: true,
            validation: {
              required: I18n.get.form.errors.required,
            },
            grid: {
              xs: 12,
            },
            params: {
              availableValues: getAvailableCollegeValues,
            },
            triggers: ["billing_address"],
          },
          {
            name: "billing_info",
            label: I18n.get.vocertCourseMoment.messages.noVoOrderer,
            type: "info",
            params: {
              dependency: {
                orderer: {
                  value: ["0"],
                  visibility: true,
                },
              },
            },
          },
          {
            name: "billing_address",
            label: I18n.get.vocertCourseMoment.labels.billingAddress,
            type: "text",
            required: true,
            validation: {
              validate: validateOrdererDependency,
            },
            grid: {
              xs: 12,
              md: 7,
            },
            params: {
              multiline: true,
              rows: 5,
              placeholder:
                I18n.get.vocertCourseMoment.placeholders.billingAddress,
              dependency: {
                orderer: {
                  value: ["0"],
                  visibility: true,
                },
              },
            },
          },
          {
            name: "billing_address_info",
            label: "",
            type: "list",
            grid: {
              xs: 12,
              md: 5,
            },
            params: {
              items: I18n.get.vocertCourseMoment.messages.billingAddressInfo,
              dependency: {
                orderer: {
                  value: ["0"],
                  visibility: true,
                },
              },
            },
          },
          {
            name: "author",
            label: I18n.get.misc.author,
            type: "entity_field",
            grid: {
              xs: 12,
            },
            params: {
              dialog: {
                renderTitleField: (item: any) => getUserString(item),
                types: ["user"],
                query: SEARCH_USER,
                category: "searchUser",
              },
              fields: {
                user: {
                  title: "name",
                },
              },
            },
          },
          {
            name: "notes",
            label: I18n.get.vocertCourseMoment.labels.describeTheOrderer,
            type: "editor",
            grid: {
              xs: 12,
            },
            params: {
              editorSettings: {
                height: 300,
                plugins: ["lists"],
                toolbar: `undo redo | removeformat | bold italic underline strikethrough |
                          forecolor | alignleft aligncenter alignright alignjustify |
                          image media link | table | bullist numlist outdent indent`,
              },
            },
          },
        ],
      },
      {
        key: "stakeholders",
        label: I18n.get.vocertCourseMoment.labels.stakeholders,
        fields: [
          {
            name: "list",
            label: "",
            type: "custom",
            grid: {
              xs: 12,
            },
            render: (data: any) => (
              <Alert severity="warning">
                {I18n.get.vocertCourseMoment.messages.stakeholdersExplanation}
              </Alert>
            ),
          },
          {
            name: "stakeholders",
            label: I18n.get.vocertCourseMoment.labels.stakeholders,
            type: "content_list",
            grid: {
              xs: 12,
            },
            params: {
              renderItemTitle: (item: any) => getUserString(item),
              dialog: {
                renderTitleField: (item: any) => getUserString(item),
                query: SEARCH_USER,
                category: "searchUser",
              },
            },
          },
        ],
      },
    );
    return tabs;
  };

  return (
    <Layout className={classes.formLayout}>
      <Form
        tabs={getTabs()}
        labels={{
          loading: I18n.get.form.labels.loadingContent,
          create: I18n.get.vocertCourseMoment.labels.createCourseMoment,
          created: I18n.get.vocertCourseMoment.messages.courseMomentSaved,
          updated: I18n.get.vocertCourseMoment.messages.courseMomentSaved,
          deleted: I18n.get.vocertCourseMoment.messages.courseMomentDeleted,
          fieldRequired: I18n.get.form.errors.required,
        }}
        operations={{
          category: "certCourseMoment",
          get: GET_COURSE_MOMENT,
          create: CREATE_COURSE_MOMENT,
          update: UPDATE_COURSE_MOMENT,
          getOnCreate: GET_AVAILABLE_GROUPS,
        }}
        variables={{ categories: ["local"], byPassMembership: true }}
        paths={{
          back: "/utbildningar",
        }}
        initialState={getInitialState()}
        pageTitleField="title"
        urlParams={urlParams}
        createParam="skapa"
        classes={{
          formWrapper: clsx("vo-global__top-bottom-space", classes.formWrapper),
          toolbar: classesForm.toolbar,
          paper: clsx(classesForm.form, classes.form),
        }}
        onComplete={handleRedirect}
        onCancel={handleRedirect}
      />
    </Layout>
  );
};

export default CourseMomentEdit;
